/**
 * Account.tsx — "My Account" self-service page.
 *
 * Rebuilt in Pass 4.
 * Uses shared UI library (Card, FormField, Input, Tag, Button, toast).
 *
 * Preserves all behavior from the original:
 *   - Reads user identity from the JWT in sessionStorage ('meridian_token'),
 *     then overlays fresh data from GET /api/users/me
 *   - Password change: current + new + confirm, client-side validation,
 *     POST /api/auth/change-password, toast on success/error
 *   - 2FA toggle placeholder (disabled until domain setup)
 *   - Active session indicator
 *
 * Error handling: known axios error shape is unwrapped to show server message;
 * unknown errors fall back to a generic message.
 */

import { useEffect, useState } from 'react';
import api from '../../api';
import { theme } from '../../theme';
import {
  Card, CardHeader, CardTitle,
  Tag, type TagKind,
  Button, Input, FormField,
  LiveDot,
  toast,
} from '../../components/ui';

/* ═══════════════ ROLE LABELS / COLORS ═══════════════ */

const ROLE_LABELS: Record<string, string> = {
  super_admin:    'Super Admin',
  client_admin:   'Client Admin',
  client_analyst: 'Client Analyst',
  reviewer:       'Reviewer',
  administrator:  'Administrator',
  admin:          'Admin',
};

/** Map role → Tag kind. Defaults to 'neutral' for unknown roles. */
const ROLE_TAG_KIND: Record<string, TagKind> = {
  super_admin:    'warning',
  client_admin:   'purple',
  client_analyst: 'cyan',
  reviewer:       'success',
  administrator:  'warning',
  admin:          'warning',
};

/* ═══════════════ PAGE ═══════════════ */

export default function Account() {
  const [userData, setUserData] = useState({
    email: '',
    role: '',
    name: '',
    client_name: null as string | null,
    company: '' as string,
  });

  // Password change state
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  useEffect(() => {
    document.title = 'My Account | Meridian Registry';
    return () => { document.title = 'Meridian Registry'; };
  }, []);

  // Load identity: start from JWT (only if it has a real email — skip Auth0
  // sub values like google-oauth2|123), then overlay with authoritative
  // /api/users/me response from the DB.
  useEffect(() => {
    const token = sessionStorage.getItem('meridian_token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const jwtEmail = typeof payload.email === 'string' && payload.email.includes('@')
          ? payload.email
          : '';
        setUserData({
          email: jwtEmail,
          role: payload.role || '',
          name: payload.name || '',
          client_name: payload.client_name || null,
          company: '',
        });
      } catch {
        // invalid token — leave state blank, the API call will fill it
      }
    }

    api
      .get('/api/users/me')
      .then(res => {
        setUserData({
          email:       res.data.email       || '',
          role:        res.data.role        || '',
          name:        res.data.name        || '',
          client_name: res.data.client_name ?? null,
          company:     res.data.company     || '',
        });
      })
      .catch(() => {
        // silent — JWT data (if any) already displayed
      });
  }, []);

  const handleChangePassword = async () => {
    setPwError('');

    if (!currentPw || !newPw) {
      setPwError('All fields are required');
      return;
    }
    if (newPw !== confirmPw) {
      setPwError('New passwords do not match');
      return;
    }
    if (newPw.length < 8) {
      setPwError('Password must be at least 8 characters');
      return;
    }

    setPwLoading(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success('Password updated successfully');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { data?: { detail?: string } } }).response;
        setPwError(resp?.data?.detail || 'Failed to change password');
      } else {
        setPwError('Failed to change password');
      }
    } finally {
      setPwLoading(false);
    }
  };

  const initials = (userData.name || userData.email || 'U').charAt(0).toUpperCase();
  const roleLabel = ROLE_LABELS[userData.role] || userData.role || '—';
  const roleTagKind = ROLE_TAG_KIND[userData.role] ?? 'neutral';

  return (
    <div style={{ padding: 32 }}>
      {/* Page header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{
          fontSize: 28, fontWeight: 600,
          color: theme.text.primary,
          margin: 0,
          letterSpacing: '-0.02em',
        }}>My Account</h1>
        <div aria-hidden style={{
          height: 3, width: 40,
          background: theme.brand.cyan,
          borderRadius: 2,
          boxShadow: `0 0 12px ${theme.brand.cyanGlow}`,
          marginTop: 6,
        }} />
        <div style={{
          fontSize: 11, fontWeight: 600,
          color: theme.text.tertiary,
          textTransform: 'uppercase',
          letterSpacing: '0.14em',
          marginTop: 8,
        }}>Manage your profile and security settings</div>
      </div>

      {/* Two-column grid — stacks on narrow screens */}
      <div
        className="meridian-account-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 20,
          alignItems: 'start',
        }}>
        <style>{`
          @media (min-width: 900px) {
            .meridian-account-grid {
              grid-template-columns: 1fr 1fr !important;
            }
          }
        `}</style>

        {/* ─── Profile Information ─── */}
        <Card padding="none">
          <CardHeader>
            <CardTitle>Profile information</CardTitle>
          </CardHeader>
          <div style={{ padding: '32px' }}>
            {/* Avatar */}
            <div style={{
              display: 'flex', justifyContent: 'center',
              marginBottom: 24,
            }}>
              <div style={{
                width: 80, height: 80,
                borderRadius: '50%',
                background: theme.brand.cyanSoft,
                border: `2px solid ${theme.brand.cyan}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, fontWeight: 700,
                fontFamily: theme.font.data,
                color: theme.brand.cyan,
                boxShadow: `0 0 24px ${theme.brand.cyanGlow}`,
              }}>{initials}</div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <ReadonlyField label="Email" value={userData.email || '—'} />
              <ReadonlyField
                label="Name"
                value={
                  userData.name
                    || (userData.email.includes('@') ? userData.email.split('@')[0] : '')
                    || '—'
                }
              />

              {/* Role as tag */}
              <div>
                <FieldLabel>Role</FieldLabel>
                <div>
                  <Tag kind={roleTagKind}>{roleLabel}</Tag>
                </div>
              </div>

              <ReadonlyField label="Client" value={userData.client_name || 'All clients'} />
              {userData.company && (
                <ReadonlyField label="Company" value={userData.company} />
              )}
            </div>
          </div>
        </Card>

        {/* ─── Security Settings ─── */}
        <Card padding="none">
          <CardHeader>
            <CardTitle>Security settings</CardTitle>
          </CardHeader>
          <div style={{ padding: '32px' }}>
            {/* 2FA placeholder */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
              paddingBottom: 18,
              borderBottom: `1px solid ${theme.borderColor.subtle}`,
              marginBottom: 20,
              gap: 16,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 14, fontWeight: 600,
                  color: theme.text.primary,
                  marginBottom: 4,
                }}>Two-factor authentication</div>
                <div style={{
                  fontSize: 12,
                  color: theme.text.secondary,
                  lineHeight: 1.5,
                  marginBottom: 6,
                }}>Add an extra layer of security to your account.</div>
                <div style={{
                  fontSize: 11, fontWeight: 600,
                  color: theme.semantic.warning,
                  letterSpacing: '0.05em',
                }}>Available after domain setup</div>
              </div>

              {/* Disabled toggle */}
              <DisabledToggle />
            </div>

            {/* Change password */}
            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontSize: 14, fontWeight: 600,
                color: theme.text.primary,
                marginBottom: 12,
              }}>Change password</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <FormField label="Current password">
                  <Input
                    type="password"
                    placeholder="Enter current password"
                    value={currentPw}
                    onChange={e => setCurrentPw(e.target.value)}
                    autoComplete="current-password"
                  />
                </FormField>
                <FormField label="New password">
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    autoComplete="new-password"
                  />
                </FormField>
                <FormField label="Confirm new password" error={pwError || undefined}>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPw}
                    onChange={e => setConfirmPw(e.target.value)}
                    autoComplete="new-password"
                    error={!!pwError}
                  />
                </FormField>

                <div>
                  <Button
                    variant="primary"
                    size="md"
                    loading={pwLoading}
                    onClick={handleChangePassword}
                    disabled={pwLoading}
                  >
                    {pwLoading ? 'Updating…' : 'Update password'}
                  </Button>
                </div>
              </div>
            </div>

            {/* Active sessions */}
            <div style={{
              paddingTop: 20,
              borderTop: `1px solid ${theme.borderColor.subtle}`,
            }}>
              <div style={{
                fontSize: 14, fontWeight: 600,
                color: theme.text.primary,
                marginBottom: 10,
              }}>Active sessions</div>
              <LiveDot color={theme.semantic.success}>
                <span style={{
                  fontSize: 13,
                  color: theme.text.secondary,
                }}>Current session · this device</span>
              </LiveDot>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ═══════════════ SUB-COMPONENTS ═══════════════ */

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label style={{
      display: 'block',
      fontSize: 11, fontWeight: 600,
      color: theme.text.tertiary,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom: 6,
    }}>{children}</label>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div style={{
        background: theme.surface.inset,
        border: `1px solid ${theme.borderColor.subtle}`,
        borderRadius: 8,
        padding: '10px 14px',
        fontSize: 14,
        color: theme.text.primary,
        fontFamily: theme.font.ui,
        minHeight: 20,
      }}>{value}</div>
    </div>
  );
}

/** Disabled 2FA toggle. Purely visual. */
function DisabledToggle() {
  return (
    <div
      role="switch"
      aria-checked="false"
      aria-disabled="true"
      title="Available after domain setup"
      style={{
        width: 40, height: 22,
        borderRadius: 11,
        background: theme.surface.cardHover,
        border: `1px solid ${theme.borderColor.subtle}`,
        position: 'relative',
        opacity: 0.5,
        cursor: 'not-allowed',
        flexShrink: 0,
      }}>
      <div style={{
        width: 16, height: 16,
        borderRadius: '50%',
        background: theme.text.quaternary,
        position: 'absolute',
        top: 2, left: 2,
      }} />
    </div>
  );
}
